// NewUserDialog.tsx
import React from "react";
import ReactDOM from "react-dom";

import { Functions } from "../../Types/Enums/Functions";
import { Permissions } from "../../Types/Enums/Permissions";
import WorkerData from "../../Types/WorkerData";

// MUI Material Icons
import PersonIcon from "@mui/icons-material/Person";
import BadgeIcon from "@mui/icons-material/Badge";
import RoomServiceIcon from "@mui/icons-material/RoomService";     // Server (waiter)
import SoupKitchenIcon from "@mui/icons-material/SoupKitchen";     // Kitchen
import WashIcon from "@mui/icons-material/Wash";                   // Dishwashing
import SecurityIcon from "@mui/icons-material/Security";
import CodeIcon from "@mui/icons-material/Code";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";

interface NewUserDialogProps {
    onClose: (data: WorkerData | null) => void;
}

interface NewUserDialogState {
    mounted: boolean;
    dark: boolean;

    firstName: string;
    lastName: string;
    func: Functions;
    perm: Permissions;

    focusFirst: boolean;
    focusLast: boolean;

    openFunc: boolean;
    openPerm: boolean;
    hiFunc: number;
    hiPerm: number;
}

export default class NewUserDialog extends React.Component<
    NewUserDialogProps,
    NewUserDialogState
> {
    private firstRef = React.createRef<HTMLInputElement>();
    private lastRef = React.createRef<HTMLInputElement>();
    private funcWrapRef = React.createRef<HTMLDivElement>();
    private permWrapRef = React.createRef<HTMLDivElement>();
    private funcBtnRef = React.createRef<HTMLButtonElement>();
    private permBtnRef = React.createRef<HTMLButtonElement>();
    private cancelRef = React.createRef<HTMLButtonElement>();
    private createRef = React.createRef<HTMLButtonElement>();
    private prevOverflow = "";

    constructor(props: NewUserDialogProps) {
        super(props);
        this.state = {
            mounted: false,
            dark: false,

            firstName: "",
            lastName: "",
            func: Functions.Server,
            perm: Permissions.User,

            focusFirst: false,
            focusLast: false,

            openFunc: false,
            openPerm: false,
            hiFunc: 0,
            hiPerm: 0,
        };
    }

    private get accent() { return "#c79656"; }
    private ring = (focused: boolean) => (focused ? `0 0 0 2px rgba(199,150,86,.28)` : "none");
    private canCreate = () => this.state.firstName.trim().length > 0 && this.state.lastName.trim().length > 0;
    private cancel = () => this.props.onClose(null);

    private create = () => {
        const first = this.state.firstName.trim();
        const last = this.state.lastName.trim();
        if (!first || !last) return;

        const w = new WorkerData(
            "",
            "",
            first,
            last,
            0,
            0,
            this.state.perm,
            this.state.func,
            0,
            0
        );

        this.props.onClose(w);
    };

    componentDidMount() {
        requestAnimationFrame(() => this.setState({ mounted: true }));
        this.firstRef.current?.focus();

        this.prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
        this.setState({ dark: !!mq?.matches });
        mq?.addEventListener?.("change", (e) => this.setState({ dark: e.matches }));

        document.addEventListener("keydown", this.handleKey);
        document.addEventListener("mousedown", this.handleGlobalMouseDown);
    }

    componentWillUnmount() {
        document.body.style.overflow = this.prevOverflow;
        document.removeEventListener("keydown", this.handleKey);
        document.removeEventListener("mousedown", this.handleGlobalMouseDown);
    }

    private handleKey = (e: KeyboardEvent) => {
        const tag = (document.activeElement as HTMLElement | null)?.tagName;
        const isInput = tag === "INPUT";
        const anyOpen = this.state.openFunc || this.state.openPerm;

        if (e.key === "Escape") {
            e.preventDefault();
            if (this.state.openFunc) return this.setState({ openFunc: false });
            if (this.state.openPerm) return this.setState({ openPerm: false });
            this.cancel();
            return;
        }

        if (e.key === "Enter" && (e.metaKey || e.ctrlKey || (!anyOpen && !isInput))) {
            e.preventDefault();
            if (this.canCreate()) this.create();
            return;
        }

        if (e.key === "Tab") {
            const stops = [
                this.firstRef.current,
                this.lastRef.current,
                this.funcBtnRef.current,
                this.permBtnRef.current,
                this.cancelRef.current,
                this.createRef.current,
            ].filter(Boolean) as HTMLElement[];
            if (!stops.length) return;
            const active = document.activeElement as HTMLElement | null;
            const idx = stops.findIndex((el) => el === active);
            if (idx !== -1) {
                e.preventDefault();
                const next = e.shiftKey ? (idx - 1 + stops.length) % stops.length : (idx + 1) % stops.length;
                stops[next].focus();
            }
        }
    };

    // Close dropdowns when clicking anywhere outside them
    private handleGlobalMouseDown = (e: MouseEvent) => {
        const t = e.target as Node;
        const inFunc = this.funcWrapRef.current?.contains(t);
        const inPerm = this.permWrapRef.current?.contains(t);
        if (!inFunc && !inPerm && (this.state.openFunc || this.state.openPerm)) {
            this.setState({ openFunc: false, openPerm: false });
        }
    };

    private handleOverlayClick = () => this.cancel();
    private stop = (e: React.MouseEvent) => e.stopPropagation();

    private funcOptions = [
        { value: Functions.Server, label: "Server", icon: RoomServiceIcon },
        { value: Functions.Kitchen, label: "Kitchen", icon: SoupKitchenIcon },
        { value: Functions.Dishwashing, label: "Dishwashing", icon: WashIcon },
    ];
    private permOptions = [
        { value: Permissions.User, label: "User", icon: PersonIcon },
        { value: Permissions.Administrator, label: "Administrator", icon: SecurityIcon },
        { value: Permissions.Developer, label: "Developer", icon: CodeIcon },
    ];

    private toggleFunc = () =>
        this.setState((s) => ({
            openFunc: !s.openFunc,
            openPerm: false,
            hiFunc: this.funcOptions.findIndex((o) => o.value === s.func),
        }));

    private togglePerm = () =>
        this.setState((s) => ({
            openPerm: !s.openPerm,
            openFunc: false,
            hiPerm: this.permOptions.findIndex((o) => o.value === s.perm),
        }));

    private chooseFunc = (idx: number) => {
        const opt = this.funcOptions[idx];
        if (!opt) return;
        this.setState({ func: opt.value, openFunc: false });
        this.permBtnRef.current?.focus();
    };

    private choosePerm = (idx: number) => {
        const opt = this.permOptions[idx];
        if (!opt) return;
        this.setState({ perm: opt.value, openPerm: false });
        this.cancelRef.current?.focus();
    };

    private onFuncKey = (e: React.KeyboardEvent) => {
        const { openFunc, hiFunc } = this.state;
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            const dir = e.key === "ArrowDown" ? 1 : -1;
            const base = openFunc ? hiFunc : this.funcOptions.findIndex((o) => o.value === this.state.func);
            const next = Math.max(0, Math.min(this.funcOptions.length - 1, base + dir));
            this.setState({ openFunc: true, hiFunc: next, openPerm: false });
        } else if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            if (!openFunc) return this.toggleFunc();
            this.chooseFunc(hiFunc);
        } else if (e.key === "Escape") {
            e.preventDefault();
            this.setState({ openFunc: false });
        }
    };

    private onPermKey = (e: React.KeyboardEvent) => {
        const { openPerm, hiPerm } = this.state;
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            const dir = e.key === "ArrowDown" ? 1 : -1;
            const base = openPerm ? hiPerm : this.permOptions.findIndex((o) => o.value === this.state.perm);
            const next = Math.max(0, Math.min(this.permOptions.length - 1, base + dir));
            this.setState({ openPerm: true, hiPerm: next, openFunc: false });
        } else if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            if (!openPerm) return this.togglePerm();
            this.choosePerm(hiPerm);
        } else if (e.key === "Escape") {
            e.preventDefault();
            this.setState({ openPerm: false });
        }
    };

    render() {
        if (typeof document === "undefined") return null;

        const accent = this.accent;
        const isDark = this.state.dark;
        const disabledCreate = !this.canCreate();

        const overlayStyle: React.CSSProperties = {
            position: "fixed",
            top: 0, right: 0, bottom: 0, left: 0,
            background: isDark ? "rgba(0,0,0,.6)" : "rgba(0,0,0,.45)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            opacity: this.state.mounted ? 1 : 0,
            transition: "opacity 140ms ease-out",
            zIndex: 9999,
        };

        // Added extra spacing via larger gaps and margins
        const dialogStyle: React.CSSProperties = {
            width: "92vw",
            maxWidth: 600,
            minHeight: 380,
            borderRadius: 18,
            background: isDark ? "#141414" : "#ffffff",
            color: isDark ? "#f5f5f5" : "#111111",
            border: "1px solid rgba(199,150,86,.35)",
            boxShadow: "0 16px 60px rgba(0,0,0,.35)",
            transform: this.state.mounted ? "translateY(0) scale(1)" : "translateY(6px) scale(.98)",
            transition: "transform 180ms ease-out",
            padding: 30,
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            gap: 22, // more space between header, content, and actions
        };

        const headerStyle: React.CSSProperties = {
            display: "flex",
            alignItems: "center",
            gap: 10,
            justifyContent: "center",
            marginTop: 4,
            marginBottom: 20,
        };
        const titleStyle: React.CSSProperties = { margin: 0, fontSize: "1.2rem", lineHeight: 1.2 };

        const formStyle: React.CSSProperties = {
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 12,
            alignItems: "start",
        };

        const labelStyle: React.CSSProperties = {
            fontSize: 12,
            opacity: 0.85,
            marginBottom: 8,
            display: "block",
        };
        const fieldCol: React.CSSProperties = { display: "flex", flexDirection: "column"};

        const inputWrap: React.CSSProperties = {
            display: "flex",
            alignItems: "center",
            gap: 8,
            border: "1px solid rgba(199,150,86,.35)",
            borderRadius: 12,
            padding: "10px 12px",
            background: isDark ? "#101010" : "#ffffff",
        };
        const inputEl = (focused: boolean): React.CSSProperties => ({
            flex: 1,
            border: "0",
            outline: "none",
            background: "transparent",
            color: isDark ? "#f5f5f5" : "#111111",
            fontSize: 14,
            boxShadow: this.ring(focused),
        });

        const iconStyle = { color: accent, fontSize: 18 };

        const selectWrap: React.CSSProperties = { position: "relative", display: "flex", flexDirection: "column" };
        const selectBtn = (focused: boolean): React.CSSProperties => ({
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            width: "100%",
            padding: "12px 14px",
            borderRadius: 12,
            border: "1px solid rgba(199,150,86,.35)",
            background: isDark ? "#101010" : "#ffffff",
            color: isDark ? "#f5f5f5" : "#111111",
            cursor: "pointer",
            outline: "none",
            boxShadow: this.ring(focused),
            fontSize: 14, // button text
        });
        const selectValueRow: React.CSSProperties = { display: "flex", alignItems: "center", gap: 8 };

        const menuStyle: React.CSSProperties = {
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            right: 0,
            background: isDark ? "#121212" : "#ffffff",
            border: "1px solid rgba(199,150,86,.35)",
            boxShadow: "0 10px 30px rgba(0,0,0,.35)",
            borderRadius: 12,
            padding: 6,
            maxHeight: 220,
            overflowY: "auto",
            zIndex: 2,
        };
        // smaller text in menu items + lighter weights
        const optionStyle = (active: boolean, selected: boolean): React.CSSProperties => ({
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            padding: "10px 12px",
            borderRadius: 10,
            background: active ? "rgba(199,150,86,.10)" : "transparent",
            fontWeight: selected ? 500 : 400,
            fontSize: 13, // smaller menu text
            cursor: "pointer",
        });

        const actionsStyle: React.CSSProperties = { display: "flex", gap: 16, marginTop: 30 };

        const btnBase: React.CSSProperties = {
            border: 0,
            borderRadius: 12,
            padding: "12px 16px",
            fontWeight: 600,
            cursor: "pointer",
            outline: "none",
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
        };
        const cancelBtnStyle: React.CSSProperties = {
            ...btnBase,
            background: "transparent",
            color: isDark ? "#f5f5f5" : "#111111",
            border: "1px solid rgba(199,150,86,.45)",
        };
        const createBtnStyle = (disabled: boolean): React.CSSProperties => ({
            ...btnBase,
            background: disabled ? "rgba(199,150,86,.45)" : accent,
            color: "#ffffff",
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.9 : 1,
        });

        const funcOpt = this.funcOptions.find((o) => o.value === this.state.func)!;
        const permOpt = this.permOptions.find((o) => o.value === this.state.perm)!;

        const dialog = (
            <div style={overlayStyle} onClick={this.handleOverlayClick} role="presentation">
                <div
                    style={dialogStyle}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="new-user-title"
                    onClick={this.stop}
                >
                    {/* Header */}
                    <div style={headerStyle}>
                        <AddIcon style={{ color: accent, fontSize: 18 }} />
                        <h2 id="new-user-title" style={titleStyle}>Create New User</h2>
                    </div>

                    {/* Form */}
                    <div style={formStyle}>
                        {/* First name */}
                        <div style={fieldCol}>
                            <label style={labelStyle} htmlFor="nu-first">First name</label>
                            <div style={{ ...inputWrap, boxShadow: this.ring(this.state.focusFirst) }}>
                                <PersonIcon style={iconStyle} />
                                <input
                                    id="nu-first"
                                    ref={this.firstRef}
                                    type="text"
                                    placeholder="firstname"
                                    value={this.state.firstName}
                                    onChange={(e) => this.setState({ firstName: e.target.value })}
                                    onFocus={() => this.setState({ focusFirst: true })}
                                    onBlur={() => this.setState({ focusFirst: false })}
                                    style={inputEl(false)}
                                />
                            </div>
                        </div>

                        {/* Last name */}
                        <div style={fieldCol}>
                            <label style={labelStyle} htmlFor="nu-last">Last name</label>
                            <div style={{ ...inputWrap, boxShadow: this.ring(this.state.focusLast) }}>
                                <BadgeIcon style={iconStyle} />
                                <input
                                    id="nu-last"
                                    ref={this.lastRef}
                                    type="text"
                                    placeholder="lastname"
                                    value={this.state.lastName}
                                    onChange={(e) => this.setState({ lastName: e.target.value })}
                                    onFocus={() => this.setState({ focusLast: true })}
                                    onBlur={() => this.setState({ focusLast: false })}
                                    style={inputEl(false)}
                                />
                            </div>
                        </div>

                        {/* Function (custom select) */}
                        <div style={fieldCol} ref={this.funcWrapRef}>
                            <label style={labelStyle} id="nu-func-label">Function</label>
                            <div style={selectWrap}>
                                <button
                                    ref={this.funcBtnRef}
                                    type="button"
                                    aria-haspopup="listbox"
                                    aria-expanded={this.state.openFunc}
                                    aria-labelledby="nu-func-label"
                                    onClick={(e) => { e.preventDefault(); this.toggleFunc(); }}
                                    onKeyDown={this.onFuncKey}
                                    style={selectBtn(false)}
                                >
                                    <span style={selectValueRow}>
                                        {React.createElement(funcOpt.icon, { style: iconStyle })}
                                        <span>{funcOpt.label}</span>
                                    </span>
                                    <ExpandMoreIcon style={{ color: isDark ? "#ddd" : "#333" }} />
                                </button>

                                {this.state.openFunc && (
                                    <div role="listbox" aria-labelledby="nu-func-label" style={menuStyle}>
                                        {this.funcOptions.map((o, i) => {
                                            const selected = o.value === this.state.func;
                                            const active = i === this.state.hiFunc;
                                            return (
                                                <div
                                                    key={o.value}
                                                    role="option"
                                                    aria-selected={selected}
                                                    onMouseEnter={() => this.setState({ hiFunc: i })}
                                                    onMouseDown={(e) => e.preventDefault()}
                                                    onClick={() => this.chooseFunc(i)}
                                                    style={optionStyle(active, selected)}
                                                >
                                                    <span style={selectValueRow}>
                                                        {React.createElement(o.icon, { style: iconStyle })}
                                                        <span>{o.label}</span>
                                                    </span>
                                                    {selected && <CheckIcon style={{ color: isDark ? "#ddd" : "#333", fontSize: 16 }} />}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Permission (custom select) */}
                        <div style={fieldCol} ref={this.permWrapRef}>
                            <label style={labelStyle} id="nu-perm-label">Permission</label>
                            <div style={selectWrap}>
                                <button
                                    ref={this.permBtnRef}
                                    type="button"
                                    aria-haspopup="listbox"
                                    aria-expanded={this.state.openPerm}
                                    aria-labelledby="nu-perm-label"
                                    onClick={(e) => { e.preventDefault(); this.togglePerm(); }}
                                    onKeyDown={this.onPermKey}
                                    style={selectBtn(false)}
                                >
                                    <span style={selectValueRow}>
                                        {React.createElement(permOpt.icon, { style: iconStyle })}
                                        <span>{permOpt.label}</span>
                                    </span>
                                    <ExpandMoreIcon style={{ color: isDark ? "#ddd" : "#333" }} />
                                </button>

                                {this.state.openPerm && (
                                    <div role="listbox" aria-labelledby="nu-perm-label" style={menuStyle}>
                                        {this.permOptions.map((o, i) => {
                                            const selected = o.value === this.state.perm;
                                            const active = i === this.state.hiPerm;
                                            return (
                                                <div
                                                    key={o.value}
                                                    role="option"
                                                    aria-selected={selected}
                                                    onMouseEnter={() => this.setState({ hiPerm: i })}
                                                    onMouseDown={(e) => e.preventDefault()}
                                                    onClick={() => this.choosePerm(i)}
                                                    style={optionStyle(active, selected)}
                                                >
                                                    <span style={selectValueRow}>
                                                        {React.createElement(o.icon, { style: iconStyle })}
                                                        <span>{o.label}</span>
                                                    </span>
                                                    {selected && <CheckIcon style={{ color: isDark ? "#ddd" : "#333", fontSize: 16 }} />}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div style={actionsStyle}>
                        <button
                            ref={this.cancelRef}
                            type="button"
                            onClick={this.cancel}
                            style={cancelBtnStyle}
                        >
                            <CloseIcon style={{ color: accent, fontSize: 16 }} />
                            Cancel
                        </button>

                        <button
                            ref={this.createRef}
                            type="button"
                            disabled={disabledCreate}
                            onClick={this.create}
                            style={createBtnStyle(disabledCreate)}
                        >
                            <AddIcon style={{ color: "#fff", fontSize: 18 }} />
                            Create
                        </button>
                    </div>
                </div>
            </div>
        );

        return ReactDOM.createPortal(dialog, document.body);
    }
}
